/** Map backend carrier-sourcing DTOs → UI models. */

export const STAGE_LABELS = {
  PROSPECT: "Prospecting",
  RATE_NEGOTIATION: "Rate Negotiation",
  VALIDATION: "Validation",
  CONTRACTING: "Contracting",
  ONBOARDING: "Onboarding",
  ACTIVE: "Active",
  INACTIVE: "Inactive",
};

export const STAGE_API = {
  Prospecting: "PROSPECT",
  "Rate Negotiation": "RATE_NEGOTIATION",
  Validation: "VALIDATION",
  Contracting: "CONTRACTING",
  Onboarding: "ONBOARDING",
  Active: "ACTIVE",
  Inactive: "INACTIVE",
};

export const PIPELINE_STAGE_OPTIONS = [
  "All Stages",
  "Prospecting",
  "Rate Negotiation",
  "Validation",
  "Contracting",
  "Onboarding",
  "Active",
];

/** Grid header label → Spring Data entity property (Pageable `sort=`). */
export const PIPELINE_SORTABLE_COLUMNS = {
  Carrier: "legalName",
  "Office Code": "officeCode",
  "MC / DOT": "mcNumber",
  Equipment: "equipmentTypes",
  Stage: "sourcingStage",
  "Onboarding Status": "onboardingCompleted",
  Risk: "riskTier",
  "Last Activity": "lastActivityAt",
  Score: "carrierScore",
};

export const PIPELINE_SORTABLE_HEADERS = Object.keys(PIPELINE_SORTABLE_COLUMNS);

export function stageLabel(stage) {
  if (!stage) return "—";
  return STAGE_LABELS[stage] || String(stage).replace(/_/g, " ");
}

export function riskLabel(tier) {
  if (!tier) return "—";
  if (tier === "MEDIUM") return "Med";
  if (tier === "LOW") return "Low";
  if (tier === "HIGH") return "High";
  if (tier === "CRITICAL") return "Critical";
  return String(tier);
}

export function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
}

export function formatDateTimeLong(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function initialsFromName(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function parseJsonList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).map((s) => s.trim()).filter(Boolean);
  const raw = String(value).trim();
  if (raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(String).map((s) => s.trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseEquipment(equipmentTypes) {
  return parseJsonList(equipmentTypes);
}

function formatCoverageRegions(coverageRegions) {
  const parts = parseJsonList(coverageRegions);
  return parts.length ? parts.join(", ") : "";
}

export function mapPipelineRow(dto) {
  return {
    id: String(dto.id),
    rawId: dto.id,
    name: dto.legalName || "—",
    initials: initialsFromName(dto.legalName),
    officeCode: dto.officeCode || "—",
    mc: dto.mcNumber || "—",
    dot: dto.dotNumber || "—",
    equipment: parseEquipment(dto.equipmentTypes),
    stage: stageLabel(dto.sourcingStage),
    stageApi: dto.sourcingStage,
    onboardStepsDone: dto.onboardingCompleted ?? 0,
    onboardStepsTotal: dto.onboardingTotal ?? 5,
    risk: riskLabel(dto.riskTier),
    riskApi: dto.riskTier,
    lastActivity: formatDateTime(dto.lastActivityAt),
    score:
      dto.carrierScore != null && dto.carrierScore !== ""
        ? Number(dto.carrierScore)
        : null,
    assignedRepEmail: dto.assignedRepEmail || "",
  };
}

export function mapCarrierDetails(dto) {
  const primary =
    (dto.contacts || []).find((c) => c.primaryContact) ||
    (dto.contacts || [])[0] ||
    null;

  return {
    id: String(dto.id),
    rawId: dto.id,
    name: dto.legalName || "—",
    initials: initialsFromName(dto.legalName),
    dba: dto.dbaName || "",
    mc: dto.mcNumber || "—",
    dot: dto.dotNumber || "—",
    scac: dto.scacCode || "",
    cityState: formatCoverageRegions(dto.coverageRegions) || "—",
    yearEstablished: null,
    equipment: parseEquipment(dto.equipmentTypes),
    fleetSize: dto.fleetSize ?? null,
    hazmat: false,
    stage: stageLabel(dto.sourcingStage),
    stageApi: dto.sourcingStage,
    onboardStepsDone: dto.onboardingCompleted ?? 0,
    onboardStepsTotal: dto.onboardingTotal ?? 5,
    risk: riskLabel(dto.riskTier),
    riskApi: dto.riskTier,
    riskScore: null,
    lastActivity: formatDateTime(dto.lastActivityAt),
    score:
      dto.carrierScore != null && dto.carrierScore !== ""
        ? Number(dto.carrierScore)
        : null,
    contact: {
      name: primary?.name || "—",
      role: primary?.title || primary?.role || "",
      phone: primary?.phone || "—",
      email: primary?.email || "—",
    },
    contacts: dto.contacts || [],
    coverageLabel: formatCoverageRegions(dto.coverageRegions) || "Coverage not set",
    notes: dto.notes || "",
    assignedRepEmail: dto.assignedRepEmail || "",
    officeCode: dto.officeCode || "",
    tags: dto.tags || "",
    stageHistory: dto.stageHistory || [],
    overview: {
      stageDetail: stageLabel(dto.sourcingStage),
      openLanes: "—",
      docsOutstanding: "—",
      complianceStatus: riskLabel(dto.riskTier)
        ? `Risk: ${riskLabel(dto.riskTier)}`
        : "Not run",
      complianceOk: dto.riskTier === "LOW",
      onboardingStatus: `${dto.onboardingCompleted ?? 0} / ${
        dto.onboardingTotal ?? 5
      } steps`,
    },
    recentActivity: null,
    threads: [],
    rateVersions: [],
    rateDocuments: [],
    laneRates: [],
    negotiationNotes: dto.notes || "",
    rateCycleDocs: [],
    documents: { carrierLevel: [], rateSpecific: [] },
    carrierOnboarding: [],
    compliance: null,
    activeLanes: 0,
  };
}

export function mapRateDocument(dto) {
  const statusMap = {
    RECEIVED: { status: "pending", statusLabel: "Received — Pending Extraction" },
    EXTRACTING: { status: "extracting", statusLabel: "Extracting…" },
    EXTRACTED: { status: "extracted", statusLabel: "Extracted" },
    FAILED: { status: "failed", statusLabel: "Extraction failed" },
    SUPERSEDED: { status: "superseded", statusLabel: "Superseded" },
  };
  const apiStatus = dto.status || "RECEIVED";
  const mapped = statusMap[apiStatus] || {
    status: "pending",
    statusLabel: apiStatus || "—",
  };
  return {
    id: String(dto.id),
    rawId: dto.id,
    name: dto.fileName || "Document",
    uploaded: formatDateTime(dto.uploadedDate),
    lanesCovered: "—",
    apiStatus,
    canStartExtraction: apiStatus === "RECEIVED" || apiStatus === "FAILED",
    extractionError: dto.extractionError || null,
    extractedAt: dto.extractedAt || null,
    ...mapped,
  };
}

export function mapRateVersion(dto) {
  return {
    id: String(dto.id),
    rawId: dto.id,
    label: dto.versionLabel || `v${dto.id}`,
    current: dto.status === "DRAFT" || dto.status === "ACTIVE",
    status: dto.status,
    notes: dto.notes || "",
  };
}

export function mapLaneRate(dto) {
  const lane = [
    [dto.originCity, dto.originState].filter(Boolean).join(", "),
    [dto.destinationCity, dto.destinationState].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" → ");

  const variance = dto.benchmarkVariancePct != null
    ? Number(dto.benchmarkVariancePct)
    : null;
  let marketTag = "good";
  let marketLabel = "At market";
  if (variance != null) {
    if (variance > 10) {
      marketTag = "bad";
      marketLabel = `${Math.round(variance)}% over market`;
    } else if (variance > 0) {
      marketTag = "warn";
      marketLabel = `${Math.round(variance)}% over market`;
    } else if (variance < 0) {
      marketTag = "good";
      marketLabel = `${Math.abs(Math.round(variance))}% under market`;
    }
  } else if (dto.benchmarkFlag) {
    marketLabel = dto.benchmarkFlag;
  }

  const onboardMap = {
    DOCS_PENDING: { rateOnboarding: "pending", rateOnboardingLabel: "Docs Pending" },
    NEGOTIATING: { rateOnboarding: "pending", rateOnboardingLabel: "Negotiating" },
    ACTIVE: { rateOnboarding: "active", rateOnboardingLabel: "Active" },
  };
  const onboard = onboardMap[dto.onboardingStatus] || {
    rateOnboarding: "pending",
    rateOnboardingLabel: dto.onboardingStatus || "—",
  };

  const contracted = dto.baseRate != null ? Number(dto.baseRate) : 0;
  const benchmark =
    dto.marketBenchmark != null
      ? Number(dto.marketBenchmark)
      : dto.historicalBenchmark != null
        ? Number(dto.historicalBenchmark)
        : contracted;

  return {
    id: String(dto.id),
    rawId: dto.id,
    lane: lane || "—",
    detail: dto.equipmentType || "",
    contracted,
    benchmark,
    benchPct: Math.min(100, Math.max(8, Math.round((contracted / (benchmark || 1)) * 80))),
    benchTone: marketTag === "bad" ? "danger" : marketTag === "warn" ? "amber" : "success",
    fuelTerms: dto.fuelTerms || "—",
    validThrough: dto.validTo || "—",
    marketTag,
    marketLabel,
    ...onboard,
    docsReceived: "—",
    signature: "—",
  };
}

export function mapThread(dto) {
  return {
    id: String(dto.id),
    rawId: dto.id,
    laneName: dto.subject || "Thread",
    meta: dto.lastEntryAt
      ? `Last activity ${formatDateTime(dto.lastEntryAt)}`
      : "No messages yet",
    scope: dto.scope,
    messages: [],
  };
}

export function mapEntry(dto) {
  const typeMap = { CALL: "call", EMAIL: "email", MEETING: "meeting", NOTE: "meeting" };
  return {
    icon: typeMap[dto.entryType] || "meeting",
    who: dto.summary || "Activity",
    txt: dto.details || dto.summary || "",
    when: formatDateTimeLong(dto.entryDate || dto.createdDate),
  };
}

export function mapOnboardingItem(dto) {
  const status = dto.status;
  let state = "todo";
  if (status === "COMPLETED") state = "done";
  else if (status === "IN_PROGRESS") state = "active";
  return {
    id: String(dto.id),
    rawId: dto.id,
    title: dto.stepLabel || dto.stepKey || "Step",
    detail: status ? String(status).replace(/_/g, " ") : "—",
    state,
    sortOrder: dto.sortOrder ?? 0,
  };
}

export const CARRIER_DOCUMENT_TYPES = [
  { value: "COI", label: "Certificate of Insurance (COI)" },
  { value: "W9", label: "W-9" },
  { value: "MSA", label: "Master Service Agreement" },
  { value: "OTHER", label: "Other" },
];

export const DOCUMENT_VERSION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export function documentTypeLabel(type) {
  const match = CARRIER_DOCUMENT_TYPES.find((t) => t.value === type);
  if (match) return match.label;
  if (!type) return "Document";
  return String(type).replace(/_/g, " ");
}

export function mapCarrierDocument(dto) {
  const received =
    String(dto.status || "").toUpperCase() === "RECEIVED" ||
    String(dto.status || "").toUpperCase() === "ACTIVE";
  const version = dto.documentVersion != null ? Number(dto.documentVersion) : 1;
  return {
    id: dto.id != null ? String(dto.id) : undefined,
    rawId: dto.id,
    name: dto.fileName || documentTypeLabel(dto.documentType),
    type: dto.documentType,
    typeLabel: documentTypeLabel(dto.documentType),
    version,
    uploaded: formatDateTime(dto.uploadedDate),
    expiryDate: dto.expiryDate || null,
    status: received ? "received" : "pending",
    statusLabel: dto.status || "Pending",
  };
}

export function mapCompliance(summary) {
  if (!summary) return null;
  const risk = summary.riskScore || {};
  const checks = (summary.checks || []).map((c) => {
    const status = String(c.status || "").toUpperCase();
    const ok = status === "PASS" || status === "PASSED" || status === "OK";
    const needsManualVerify =
      status === "MANUAL" || status === "PENDING" || status === "FAIL";
    const checkType = String(c.checkType || "").toUpperCase();
    let detail = c.status || "";
    let message = "";
    let insurance = null;
    if (c.payloadJson) {
      try {
        const payload =
          typeof c.payloadJson === "string"
            ? JSON.parse(c.payloadJson)
            : c.payloadJson;
        message = payload?.message || "";
        if (
          checkType === "INSURANCE" ||
          payload?.source === "INSURANCE_OCR"
        ) {
          insurance = {
            fileName: payload.fileName || null,
            documentId: payload.documentId || null,
            insuredName: payload.insuredName || null,
            mcNumber: payload.mcNumber || null,
            policyNumber: payload.policyNumber || null,
            policyStartDate: payload.policyStartDate || null,
            policyEndDate:
              payload.policyEndDate || c.insuranceExpiry || null,
            insurerName: payload.insurerName || null,
            identityMatched: payload.identityMatched === true,
            matchReason: payload.matchReason || null,
          };
        }
        const bits = [
          message,
          payload?.authorityStatus ? `Authority: ${payload.authorityStatus}` : null,
          payload?.safetyRating ? `Safety: ${payload.safetyRating}` : null,
          payload?.provider ? `Provider: ${payload.provider}` : null,
          insurance?.insuredName ? `Insured: ${insurance.insuredName}` : null,
          insurance?.mcNumber ? `MC: ${insurance.mcNumber}` : null,
          insurance?.policyStartDate || insurance?.policyEndDate
            ? `Policy: ${insurance.policyStartDate || "—"} → ${insurance.policyEndDate || "—"}`
            : null,
          payload?.verificationNotes
            ? `Notes: ${payload.verificationNotes}`
            : null,
        ].filter(Boolean);
        detail = bits.length ? bits.join(" · ") : c.payloadJson;
      } catch {
        detail = c.payloadJson;
      }
    } else if (c.insuranceExpiry) {
      insurance = {
        policyEndDate: c.insuranceExpiry,
        identityMatched: false,
        matchReason:
          "Insurance expiry is on file, but carrier identity (MC or name) has not been validated via COI.",
      };
      detail = `Expires: ${c.insuranceExpiry}`;
    }
    return {
      id: c.id != null ? String(c.id) : undefined,
      rawId: c.id,
      ok,
      needsManualVerify,
      apiStatus: status,
      checkType,
      title: (c.checkType || "Check").replace(/_/g, " "),
      detail,
      message,
      insurance,
      insuranceExpiry: c.insuranceExpiry || insurance?.policyEndDate || null,
    };
  });

  const insuranceCheck =
    checks.find((c) => c.checkType === "INSURANCE" && c.insurance) || null;

  return {
    lastRefreshed: formatDateTime(risk.calculatedAt),
    riskScore: risk.score ?? null,
    riskTier: risk.riskTier,
    checks,
    insurance: insuranceCheck?.insurance || null,
    basicScores: [],
  };
}

export function mapFunnelReport(dto) {
  const counts = dto?.stageCounts || {};
  const order = [
    "PROSPECT",
    "RATE_NEGOTIATION",
    "VALIDATION",
    "CONTRACTING",
    "ONBOARDING",
    "ACTIVE",
  ];
  const funnel = order.map((key) => ({
    label: stageLabel(key),
    count: Number(counts[key] || 0),
  }));
  const max = Math.max(...funnel.map((f) => f.count), 1);
  return {
    periodLabel: "Live · All lanes",
    totalCarriers: dto?.totalCarriers ?? funnel.reduce((n, f) => n + f.count, 0),
    kpis: [
      {
        lab: "Carriers in Pipeline",
        val: String(dto?.totalCarriers ?? 0),
        unit: "",
        delta: "From funnel report",
        deltaTone: "muted",
      },
      {
        lab: "Prospecting",
        val: String(counts.PROSPECT || 0),
        unit: "",
        delta: "Current stage",
        deltaTone: "muted",
      },
      {
        lab: "In Negotiation",
        val: String(counts.RATE_NEGOTIATION || 0),
        unit: "",
        delta: "Current stage",
        deltaTone: "muted",
      },
      {
        lab: "Active",
        val: String(counts.ACTIVE || 0),
        unit: "",
        delta: "Current stage",
        deltaTone: "muted",
      },
    ],
    funnel: funnel.map((f) => ({
      ...f,
      width: `${Math.max(8, Math.round((f.count / max) * 100))}%`,
    })),
    coverageGaps: [],
  };
}

export function mapCoverageGaps(list = []) {
  return list.map((g) => {
    const lane = [
      g.originState || "?",
      g.destinationState || "?",
    ].join(" → ");
    const equip = g.equipmentType ? ` (${g.equipmentType})` : "";
    const count = Number(g.prospectCount || 0);
    return {
      lane: `${lane}${equip}`,
      tag: count === 0 ? "bad" : count === 1 ? "warn" : "good",
      label:
        count === 0
          ? "No contracted carrier"
          : count === 1
            ? "1 carrier — single point of failure"
            : `${count} carriers`,
    };
  });
}

/** "Charlotte, NC" → { city, state } */
export function splitCityState(value) {
  const raw = (value || "").trim();
  if (!raw) return { city: "", state: "" };
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { city: parts[0], state: parts[parts.length - 1] };
  }
  return { city: raw, state: "" };
}

export function apiErrorMessage(err, fallback = "Request failed") {
  const status = err?.response?.status;
  const data = err?.response?.data;
  // Backend ErrorResponse uses `content` for the human-readable text
  const raw =
    (typeof data?.content === "string" && data.content) ||
    (typeof data?.message === "string" && data.message) ||
    (typeof data?.error === "string" && data.error) ||
    null;
  // Ignore bare status codes like "500" that sometimes leak as the message body
  const serverMsg =
    raw && !/^\d{3}$/.test(String(raw).trim()) ? raw : null;

  if (status === 403) {
    return (
      serverMsg ||
      "Forbidden — your role needs the Carrier Sourcing privilege for this action (VIEW / CREATE / EDIT). Assign it under Manage Team → Role privileges, then log out and back in."
    );
  }
  if (status === 401) {
    return "Session expired or unauthorized. Please log in again.";
  }
  if (status === 409) {
    return (
      serverMsg ||
      "Conflict — this carrier may already exist, or a field could not be saved (check MC/DOT and equipment/coverage). Try different identifiers or leave optional JSON fields empty and retry."
    );
  }
  if (status === 500) {
    return (
      serverMsg ||
      "Server error while uploading. Confirm migrations V307/V309 ran (document_version + file_content), then retry."
    );
  }
  return serverMsg || err?.message || fallback;
}
